/**
 * B6 · La tarjeta del SIGUIENTE nivel — el momento de conversión.
 *
 * ## Dos decisiones que la hacen funcionar
 *
 * 1. **No es un modal que se cierra y se pierde.** La oferta vive en una fila
 *    (`stage_offer`) con caducidad y estado de descarte, así que sobrevive a la
 *    recarga y reaparece hasta que el alumno decide. Un descuento que solo se
 *    ve una vez no convierte.
 * 2. **Sin precio también se muestra.** Si la caja está apagada o el carril de
 *    prepago aún no ha aterrizado, el servidor manda la oferta sin `quote` y la
 *    tarjeta degrada a «Continúa con el siguiente nivel» sin bloque de precio.
 *    Ese es el sentido de la arista blanda `billing.core?`: apagar la caja no
 *    puede secuestrar la oferta.
 *
 * El ahorro llega en USD y en la moneda secundaria **con la tasa usada**, tal y
 * como lo declara el contrato `UpsellQuote` del servidor. Aquí no se calcula
 * ningún importe: los céntimos vienen enteros del servidor a propósito.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Sparkles, X } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@/lib/u-router";
import { useOffers } from "./use-progression";
import type { PendingOffer } from "./api";

/** Cuántas cuotas sustituye la oferta (extraído para que `msg` reciba una variable). */
function instalmentsLabel(t: ReturnType<typeof useI18n>["t"], count: number): string {
  return t(msg`${count} monthly instalments`);
}

/** Importe en unidad menor → texto. Sin aritmética de floats sobre el dinero. */
function money(minor: number, currency: string): string {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}

const OfferCard: Component<{ offer: PendingOffer; onDismiss: () => void }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const quote = (): PendingOffer["quote"] => props.offer.quote ?? null;
  const nextTitle = (): string => props.offer.next_stage_title ?? "";
  /** Titular de la tarjeta. Extraído para que `msg` reciba una VARIABLE. */
  const heading = (): string => {
    const title = nextTitle();
    return title ? t(msg`Keep going: ${title}`) : t(msg`Keep going with the next level`);
  };

  return (
    <article
      data-testid="next-stage-offer"
      class="relative rounded-2xl border border-accent/40 bg-accent/5 p-5"
    >
      <button
        type="button"
        data-testid="offer-dismiss"
        class="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t(msg`Dismiss this offer`)}
        onClick={() => props.onDismiss()}
      >
        <X class="size-4" aria-hidden="true" />
      </button>

      <div class="flex items-start gap-3">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <Sparkles class="size-5" aria-hidden="true" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="font-semibold">{heading()}</h3>

          <Show
            when={quote()}
            fallback={
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Ask your academy about joining the next level.`)}
              </p>
            }
          >
            {(q) => (
              <dl class="mt-2 space-y-1 text-sm">
                <div class="flex gap-2">
                  <dt class="text-muted-foreground">{t(msg`Price`)}</dt>
                  <dd class="font-medium">{money(q().offer_amount_usd_minor, "USD")}</dd>
                </div>
                <Show when={q().savings_usd_minor > 0}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`You save`)}</dt>
                    <dd class="font-medium">
                      {money(q().savings_usd_minor, "USD")} ({Math.round(q().savings_pct)}%)
                    </dd>
                  </div>
                </Show>
                <Show when={q().secondary_amount_minor != null}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`In local currency`)}</dt>
                    <dd class="font-medium">
                      {money(q().secondary_amount_minor ?? 0, "VES")}
                      <Show when={q().exchange_rate}>
                        {(rate) => {
                          const shown = rate().toFixed(2);
                          return (
                            <span class="ml-1 text-muted-foreground">
                              {t(msg`(rate ${shown})`)}
                            </span>
                          );
                        }}
                      </Show>
                    </dd>
                  </div>
                </Show>
                <Show when={q().installments_replaced > 0}>
                  <div class="flex gap-2">
                    <dt class="text-muted-foreground">{t(msg`Instead of`)}</dt>
                    <dd class="font-medium">{instalmentsLabel(t, q().installments_replaced)}</dd>
                  </div>
                </Show>
              </dl>
            )}
          </Show>

          <Show when={props.offer.valid_until}>
            {(until) => {
              const when = new Date(until()).toLocaleDateString();
              return (
                <p class="mt-2 text-xs text-muted-foreground">{t(msg`Available until ${when}`)}</p>
              );
            }}
          </Show>

          <Button
            class="mt-3"
            data-testid="offer-cta"
            onClick={() => navigate({ to: quote()?.checkout_path ?? "/payments" })}
          >
            {t(msg`I want the next level`)}
          </Button>
        </div>
      </div>
    </article>
  );
};

/** Todas las ofertas vigentes del alumno. Vacío = no renderiza nada. */
export const NextStageOffers: Component = () => {
  const { query, dismiss } = useOffers();
  return (
    <Show when={(query.data ?? []).length > 0}>
      <div class="space-y-3">
        <For each={query.data ?? []}>
          {(offer) => <OfferCard offer={offer} onDismiss={() => void dismiss(offer.offer_id)} />}
        </For>
      </div>
    </Show>
  );
};
